/* ============================================================================
 * portalStorage.js — Supabase Storage helpers for portal photo uploads.
 *
 * Bucket: `portal-photos` (public read; anon + authenticated INSERT;
 *         authenticated UPDATE/DELETE). Created by sql/003_storage_setup.sql.
 *
 * Goals:
 *   - Replace inline data URL storage with real public object URLs.
 *   - Work for BOTH:
 *       a) Authenticated contractor uploads (Client Portal photo sharing).
 *       b) Anonymous Crew Portal uploads (no Supabase auth session).
 *
 * Public API:
 *   uploadPortalPhoto(file, { scope, ownerId }) -> { ok, url, path, error }
 *   deletePortalPhoto(urlOrPath) -> { ok, error }
 *   isStorageUrl(url) -> boolean
 *
 * Failure modes:
 *   - 4xx from anon (e.g. bucket missing or anon policy not applied)
 *     → returns { ok: false, error } so callers can fall back to data URLs.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

export const PORTAL_PHOTOS_BUCKET = 'portal-photos';

/* Allowed scopes — keep paths organized inside the bucket. */
export const PORTAL_PHOTO_SCOPES = ['client', 'crew', 'job', 'company'];

const safeExt = (file) => {
  const fromMime = (file?.type || '').split('/')[1];
  if (fromMime && /^[a-z0-9]+$/i.test(fromMime)) {
    if (fromMime === 'jpeg') return 'jpg';
    return fromMime.toLowerCase();
  }
  const fromName = (file?.name || '').split('.').pop();
  if (fromName && /^[a-z0-9]{1,5}$/i.test(fromName)) return fromName.toLowerCase();
  return 'jpg';
};

const safeOwnerSegment = (ownerId) => {
  const raw = String(ownerId || 'anon');
  return raw.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64) || 'anon';
};

const randSuffix = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().split('-')[0];
  }
  return Math.random().toString(36).slice(2, 10);
};

/**
 * Returns true when `url` looks like a Supabase Storage public URL for the
 * portal-photos bucket. Used by callers to decide whether to attempt
 * server-side deletion.
 */
export const isStorageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return (
    url.includes(`/storage/v1/object/public/${PORTAL_PHOTOS_BUCKET}/`) ||
    url.includes(`/storage/v1/object/${PORTAL_PHOTOS_BUCKET}/`)
  );
};

/**
 * Extract the in-bucket path from a public URL.
 * Returns null if it cannot be parsed.
 */
export const pathFromPublicUrl = (url) => {
  if (!isStorageUrl(url)) return null;
  const marker = `/storage/v1/object/public/${PORTAL_PHOTOS_BUCKET}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  const tail = url.slice(idx + marker.length);
  // Strip query params if any
  return tail.split('?')[0];
};

/**
 * Upload a File / Blob to Supabase Storage and return its public URL.
 *
 * @param {File|Blob} file
 * @param {{ scope: 'client'|'crew'|'job', ownerId?: string, subpath?: string, targetPath?: string }} opts
 *   - scope: top-level folder ("client", "crew", "job"). Use `targetPath` to override.
 *   - ownerId: id segment under scope (clientId / crewId / jobId).
 *   - subpath: optional middle segment ("photos", "docs", etc).
 *   - targetPath: complete bucket-relative path; takes precedence over scope/ownerId/subpath.
 * @returns {Promise<{ok:boolean, url?:string, path?:string, error?:string}>}
 */
export const uploadPortalPhoto = async (
  file,
  { scope = 'crew', ownerId = 'anon', subpath = '', targetPath = null } = {}
) => {
  if (!file) return { ok: false, error: 'no_file' };
  if (!targetPath && !PORTAL_PHOTO_SCOPES.includes(scope)) {
    return { ok: false, error: 'invalid_scope' };
  }
  try {
    const ext = safeExt(file);
    let path;
    if (targetPath) {
      path = String(targetPath).replace(/^\/+/, '');
    } else {
      const owner = safeOwnerSegment(ownerId);
      // For 'job' scope, map to plural prefix 'jobs/' for clarity.
      const topFolder = scope === 'job' ? 'jobs' : scope;      const segments = [topFolder, owner];
      if (subpath) segments.push(String(subpath).replace(/[^a-zA-Z0-9_/-]/g, ''));
      segments.push(`${Date.now()}-${randSuffix()}.${ext}`);
      path = segments.join('/');
    }

    const { error } = await supabase.storage
      .from(PORTAL_PHOTOS_BUCKET)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || `image/${ext === 'jpg' ? 'jpeg' : ext}`,
      });
    if (error) {
      return { ok: false, error: error.message || 'upload_failed' };
    }
    const { data: pub } = supabase.storage
      .from(PORTAL_PHOTOS_BUCKET)
      .getPublicUrl(path);
    if (!pub?.publicUrl) {
      return { ok: false, error: 'no_public_url' };
    }
    return { ok: true, url: pub.publicUrl, path };
  } catch (e) {
    return { ok: false, error: e?.message || 'upload_exception' };
  }
};

/**
 * Resolve a short-lived SIGNED url for an in-bucket path, using the current
 * (authenticated) session. Works for the private portal-photos bucket because
 * migration 021 grants authenticated SELECT ("so the dashboard can
 * sign/preview"). Returns null on failure (e.g. anonymous caller) so callers
 * can fall back to a stored public URL.
 */
export const createSignedPortalUrl = async (path, ttlSeconds = 3600) => {
  const clean = String(path || '').replace(/^\/+/, '');
  if (!clean || clean.startsWith('data:') || clean.startsWith('blob:')) {
    return null;
  }
  try {
    const { data, error } = await supabase.storage
      .from(PORTAL_PHOTOS_BUCKET)
      .createSignedUrl(clean, ttlSeconds);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
};

/**
 * Batch variant of createSignedPortalUrl: one round trip for many paths.
 * Returns { [path]: signedUrl }; paths that fail to sign are omitted so
 * callers can fall back per-file.
 */
export const createSignedPortalUrls = async (paths, ttlSeconds = 3600) => {
  const clean = Array.from(
    new Set(
      (paths || [])
        .map((p) => String(p || '').replace(/^\/+/, ''))
        .filter((p) => p && !p.startsWith('data:') && !p.startsWith('blob:'))
    )
  );
  if (clean.length === 0) return {};
  try {
    const { data, error } = await supabase.storage
      .from(PORTAL_PHOTOS_BUCKET)
      .createSignedUrls(clean, ttlSeconds);
    if (error || !Array.isArray(data)) return {};
    const out = {};
    data.forEach((row) => {
      if (row?.path && row?.signedUrl) out[row.path] = row.signedUrl;
    });
    return out;
  } catch {
    return {};
  }
};

/**
 * Best-effort deletion of a portal photo from Storage. Accepts either a
 * full public URL or an in-bucket path. Silently succeeds if the URL is
 * not a Storage URL (e.g. legacy data URL).
 */
export const deletePortalPhoto = async (urlOrPath) => {
  if (!urlOrPath) return { ok: true };
  let path = urlOrPath;
  if (isStorageUrl(urlOrPath)) {
    path = pathFromPublicUrl(urlOrPath);
  }
  if (!path || path.startsWith('data:') || path.startsWith('blob:')) {
    // Legacy data URL or unparsable — nothing to delete server-side.
    return { ok: true };
  }
  try {
    const { error } = await supabase.storage
      .from(PORTAL_PHOTOS_BUCKET)
      .remove([path]);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.message || 'delete_exception' };
  }
};

/**
 * Convenience: upload many files, returning {url, error} per input file.
 */
export const uploadManyPortalPhotos = async (files, opts) => {
  const arr = Array.from(files || []);
  const results = await Promise.all(arr.map((f) => uploadPortalPhoto(f, opts)));
  return results;
};

/* ============================================================================
 * Signed-URL resolution for the PRIVATE bucket (sql/021 cutover).
 *
 * When REACT_APP_PORTAL_PHOTOS_PRIVATE === 'true', the portal-photos bucket is
 * private and public URLs no longer resolve. Anonymous portal visitors get
 * short-lived signed URLs from the `portal-photo-url` Edge Function, which
 * validates their portal code before signing.
 *
 * This whole block is INERT until the flag is set, so shipping it does not
 * change current behavior. See sql/021 for the cutover runbook.
 * ========================================================================== */
// Direct read — CRA inlines process.env.REACT_APP_* at build time. (A
// `typeof process` guard short-circuits to false in the browser bundle, which
// would leave this flag permanently off and silently break the #4 cutover.)
const PORTAL_PHOTOS_PRIVATE =
  process.env.REACT_APP_PORTAL_PHOTOS_PRIVATE === 'true';

/** Best-effort: resolve the in-bucket path for a stored photo record. */
const photoStoragePath = (photo) => {
  if (!photo) return null;
  if (photo.path) return String(photo.path).replace(/^\/+/, '');
  const url = photo.url;
  if (typeof url === 'string' && isStorageUrl(url)) return pathFromPublicUrl(url);
  return null;
};

/**
 * Given a portal code + scope ('client'|'crew') and an array of photo records,
 * return the same records with `url` replaced by a fresh signed URL for any
 * photo backed by a private-bucket object. Photos that are data URLs or whose
 * path can't be resolved are returned unchanged. Falls back to the original
 * records if the flag is off or the function is unreachable.
 *
 * Uses supabase.functions.invoke() (NOT a raw fetch) so the anon apikey +
 * Authorization headers are attached — the Supabase function gateway rejects
 * unauthenticated calls with a 401 otherwise.
 */
export const resolvePortalPhotos = async (code, scope, photos) => {
  const list = Array.isArray(photos) ? photos : [];
  if (!PORTAL_PHOTOS_PRIVATE || list.length === 0) return list;

  const paths = Array.from(
    new Set(list.map(photoStoragePath).filter(Boolean))
  );
  if (paths.length === 0) return list;

  try {
    const { data, error } = await supabase.functions.invoke('portal-photo-url', {
      body: { code, scope, paths },
    });
    if (error || !data || !data.urls) return list;
    const urls = data.urls;
    return list.map((p) => {
      const path = photoStoragePath(p);
      return path && urls[path] ? { ...p, url: urls[path] } : p;
    });
  } catch (e) {
    // Pre-deploy / offline: leave records as-is.
    return list;
  }
};
